import { UserProfile } from 'api/account/models/UserProfile';
import { BudgetPDFVisibility, ElementDto, ElementHistoryDto, ElementHistoryReason, ElementListDto, ElementParameterDto, FamilySelectModel } from 'api/elements/Models/ElementDto';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import { MoneyInputController } from 'common/components/moneyInput/MoneyInput';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';
import { TextInputController } from 'common/components/textInput/TextInput';
import { Header, Label, Text } from 'common/components/texts/Texts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt, FaHistory } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import Loading from 'common/services/Loading';
import toast from 'react-hot-toast';
import { LOGGER_LOG_TYPE, DATE_TIME_FORMAT_DEFAULT } from 'Config';
import Logger from 'common/services/Logger';
import ElementsService from 'api/elements/ElementsService';
import UnitsService from 'api/units/UnitsService';
import TaxesService from 'api/taxes/TaxesService';
import { markDeletedTaxes } from 'api/taxes/taxCatalogUtils';
import { UnitDto } from 'api/units/models/UnitDto';
import { TaxDto } from 'api/taxes/models/TaxDto';
import WorkTypesService from 'api/workTypes/WorkTypesService';
import { RichInputController } from 'common/components/richTextInput/RichTextInput';
import FamiliesService from 'api/families/FamiliesService';
import { ElementTypeDto } from 'api/elements/Models/ElementTypeDto';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import { FileInputController } from 'common/components/fileInput/FileInput';
import FilesUploader from 'common/components/filesUploader/FilesUploader';
import { AttachmentDto } from 'api/common/models/AttachmentDto';
import CustomFile from 'common/models/CustomFile';
import { FamilyDto } from 'api/families/models/FamilyDto';
import Utils from 'common/services/Utils';
import styles from './ElementScreen.module.scss'
import UsersService from 'api/users/UsersService';
import Toast from 'common/services/Toast';
import { MultiLineDynamicListController } from 'common/components/multiLineDynamicList/MultiLineDynamicList';
import FamilySelector, { FamilySelectInputOption } from 'common/components/familySelector/FamilySelector';
import { ParameterSectionParameterType, ParameterSectionType, ParameterVisibility } from 'common/components/parametersSections/models';
import { Form, FormRef } from 'common/components/form/Form';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import ImportFamilyImagesModal, { ImportFamilyImagesResult } from 'common/components/modal/importFamilyImagesModal/ImportFamilyImagesModal';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import { MediaDto } from 'api/medias/MediasDto';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import HistoryModal from 'common/components/modal/historyModal/HistoryModal';
import DateFormat from 'common/components/dateFormat/dateFormat';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { SortableTree } from 'common/components/sortableTree/SortableTree';
import { ElementParameterDataProvider } from './ElementParameterDataProvider';
import ElementParameterLine from './ElementParameterLine';
import { FlattenedItem } from 'common/components/sortableTree/types';
import { AtLeast } from 'common/types/Atleast';
import { onMove } from '../utils';
import ListingTableCache, { DEFAULT_TABLE_DATA } from 'common/components/listingTable/ListingTableCache';
import { createPageId } from '../list/ElementsListScreen';
import { FamiliesListType } from 'screens/library/families/list/FamiliesListScreen';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';

interface Props {
    listType: FamiliesListType;
}

// Helper function to check if a parameter has a value
function parameterHasValue(param: ElementParameterDto): boolean {
    // Sections don't have values themselves, they organize other parameters
    if (param.type === ParameterSectionType.SECTION) {
        return false; // Sections are added in the second pass only if they have children with values
    }

    // Check based on parameter type
    switch (param.parameterType) {
        case ParameterSectionParameterType.TEXT:
            return !!param.text && param.text.trim() !== '';
        case ParameterSectionParameterType.NUMBER:
            return param.number !== null && param.number !== undefined;
        case ParameterSectionParameterType.QUANTITY:
            return param.quantity !== null && param.quantity !== undefined;
        case ParameterSectionParameterType.IMAGES:
            return !!(param.images && param.images.length > 0 && param.images.some(img => !img.remove));
        case ParameterSectionParameterType.PRODUCTS:
            return !!(param.products && param.products.length > 0);
        case ParameterSectionParameterType.NUMBER_MIN_MAX:
            return !!param.minMax && (
                (param.minMax.minValue !== null && param.minMax.minValue !== undefined) ||
                (param.minMax.maxValue !== null && param.minMax.maxValue !== undefined) ||
                (!!param.minMax.products && param.minMax.products.length > 0)
            );
        case ParameterSectionParameterType.SUPPLIER:
            return !!param.supplierId;
        case ParameterSectionParameterType.DATE:
            return !!param.dateValue;
        case ParameterSectionParameterType.PRICE:
            return !!(param.price && (param.price.price !== null && param.price.price !== undefined));
        case ParameterSectionParameterType.WEIGHT:
            return param.number !== null && param.number !== undefined;
        default:
            return false;
    }
}

// Filter parameters tree to show only those with values (for details view)
function filterParametersWithValues(parameters: ElementParameterDto[]): ElementParameterDto[] {
    const keysWithValues = new Set<string>();

    // First pass: identify all parameters with values
    parameters.forEach(param => {
        if (parameterHasValue(param)) {
            keysWithValues.add(param.key);
        }
    });

    // Second pass: add parent sections if they have children with values
    parameters.forEach(param => {
        if (param.type === ParameterSectionType.SECTION) {
            const hasChildrenWithValues = parameters.some(child =>
                child.parentKey === param.key && keysWithValues.has(child.key)
            );
            if (hasChildrenWithValues) {
                keysWithValues.add(param.key);
                // Also add all parent sections up the tree
                let currentKey = param.parentKey;
                while (currentKey) {
                    keysWithValues.add(currentKey);
                    const parent = parameters.find(p => p.key === currentKey);
                    currentKey = parent?.parentKey ?? null;
                }
            }
        }
    });

    // Third pass: filter parameters
    return parameters.filter(param => keysWithValues.has(param.key));
}

function ElementScreen({ listType }: Props): JSX.Element | null {
    const PAGE_ID = createPageId(listType)
    const listTypeLabel = listType === 'elements' ? 'element' : 'ouvrage';
    const isElement = listType === 'elements';
    const isComponent = listType === 'components'
    const { id, type } = useParams<{ id: string, type: 'create' | 'details' | 'edit' | 'duplicate' }>();
    const { t, i18n } = useTranslation()
    const navigate = useNavigate();

    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showConfirmParametersModal, setShowConfirmParametersModal] = useState(false);
    const [showConfirmDeleteFamilyModal, setShowConfirmDeleteFamilyModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showConfirmUpdateCoefficientModal, setShowConfirmUpdateCoefficientModal] = useState(false);
    const [previousElementTypeId, setPreviousElementTypeId] = useState<string | null>(null);
    const [newElementTypeId, setNewElementTypeId] = useState<string | null>(null);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [taxes, setTaxes] = useState<TaxDto[]>([]);
    const [workTypes, setWorkTypes] = useState<SelectInputOption[]>([]);
    const [elementTypes, setElementTypes] = useState<ElementTypeDto[]>([]);
    const [families, setFamilies] = useState<FamilyDto[]>([]);
    const [priceTagLabel, setPriceTagLabel] = useState<string>('€')
    const [medias, setMedias] = useState<AttachmentDto[]>([]);
    const [file, setFile] = useState<AttachmentDto | null>();

    const [parameters, setParameters] = useState<ElementParameterDto[]>([])

    // Filter parameters to show only those with values when in details mode
    const displayParameters = useMemo(() => {
        if (isDetails) {
            return filterParametersWithValues(parameters);
        }
        return parameters;
    }, [parameters, isDetails]);

    const [familyItemChanged, setFamilyItemChanged] = useState<FamilySelectModel | null>(null);
    const [newFamilies, setNewFamilies] = useState<FamilySelectModel[]>([])
    const [elementHistory, setElementHistory] = useState<ElementHistoryDto[]>([]);

    const [showImportImagesModal, setShowImportImagesModal] = useState(false);
    const [familyImagesToImport, setFamilyImagesToImport] = useState<MediaDto[]>([]);
    const [selectedFamilyName, setSelectedFamilyName] = useState('');

    const totalPrice = useRef(0)
    const [showTotalPriceInformationModal, setShowTotalPriceInformationModal] = useState(false);
    const form = useForm<ElementDto>({ shouldUnregister: false, shouldFocusError: true });
    const errors = form.formState.errors;
    const formRef = useRef<FormRef>(null);
    const isUpdatingFromFormula = useRef(false);
    const previousPriceValue = useRef<number | null | undefined>(null);

    // const [familyTypeSelected, setFamilyTypeSelected] = useState< 'both' | 'element' | 'component' | null>(null);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['ELEMENTS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['ELEMENTS_READ'])

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t(`common.new_title.${listTypeLabel}`)
        : type === 'edit'
            ? t(`common.edit_title.${listTypeLabel}`)
            : t(`common.details_title.${listTypeLabel}`);
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;
    const url = `/library/${listType}-items`;
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    const [refreshKey, setRefreshKey] = useState(0);
    const [totalRefreshKey, setTotalRefreshKey] = useState(0);

    const [selectedSectionKey, setSelectedSectionKey] = useState<string>('');
    const [priceExpression, setPriceExpression] = useState<string | null>(null);
    const [catalogElements, setCatalogElements] = useState<ElementListDto[]>([]);
    const [showReplacePriceFormulaModal, setShowReplacePriceFormulaModal] = useState(false);
    const [pendingPriceExpression, setPendingPriceExpression] = useState<string | null>(null);

    useEffect(() => {
        void getData();
    }, [id, type]);

    const getData = async () => {
        try {
            Loading.show();
            let result: Partial<ElementDto> = {
                id,
                name: '',
                families: [{ id: Utils.newGuid(), familyId: null, subFamilyId: null , isComponent: false,isElement: false }],
                imagesToRemove: [],
                budgetPdfVisibility: BudgetPDFVisibility.PRICE_INVISIBLE
            }
            const familyIds: string[] = []
            if (id) {
                // Always fetch all parameters (false), filtering will be done in the frontend
                result = await ElementsService.getById(id, false);

                setMedias(result.images ?? [])
                setFile(result.attachment)

                if (result?.parameters?.length) {
                    result.parameters.forEach((x) => {
                        x.isOpen = true;
                        if (x.parameterType === ParameterSectionParameterType.QUANTITY) {
                            x.unitSymbol = units.find(x => x.id === result.unitId)?.symbol
                        }
                    })
                }

                if (result.families?.length) {
                    result.families.forEach((x) => {
                        if (x.familyId) familyIds.push(x.familyId)
                        if (x.subFamilyId) familyIds.push(x.subFamilyId)
                    })
                }
                if (result && type === 'duplicate') {
                    result.name += ' - ' + t('common.copy');
                }
            }

            // Keep a deactivated tax already applied to this element visible in edit/details.
            const referencedTaxIds = id && result.taxId ? [result.taxId] : undefined;

            const [
                unitsData,
                taxesData,
                workTypesData,
                familiesData,
                elementTypesData,
                catalogElementsData
            ] = await Promise.all([
                UnitsService.getFormattedCatalog(),
                TaxesService.getFormattedCatalog(referencedTaxIds),
                WorkTypesService.getCatalog({ workTypeId: result.workTypeId }),
                FamiliesService.getElementsCatalog({ familyIds,isComponent,isElement }),
                ElementsService.getElementTypesCatalog(),
                ElementsService.getAllByCriteria({}),
            ]);

            setUnits(unitsData)
            setTaxes(markDeletedTaxes(taxesData, t('common.deleted')))
            setWorkTypes(workTypesData)
            setElementTypes(elementTypesData)
            setFamilies(familiesData)
            // Other catalog elements that can be referenced by reference/name in the price formula
            setCatalogElements(catalogElementsData?.filter(e => e.id !== id) ?? [])

            const tag = unitsData.find(y => y.id === result.unitId)?.symbol
            setPriceTagLabel('€' + (tag ? `/${tag}` : ''));
            setPriceExpression(result.priceExpression ?? null);
            previousPriceValue.current = result.price;
            if (result.priceExpression) {
                isUpdatingFromFormula.current = true;
            }
            form.reset(result);

            // Set parameters AFTER form.reset and setPriceExpression so the useEffect
            // that watches [parameters] fires with all data already available
            if (result?.parameters?.length) {
                setParameters(result.parameters);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get elements', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const onOpenHistory = async () => {
        if (id) {
            await getElementHistories(id);
        }
        setShowHistoryModal(true);
    }

    const getElementHistories = async (elementId: string) => {
        try {
            Loading.show();

            const elementHistoryData = await ElementsService.getElementHistory(elementId);
            setElementHistory(elementHistoryData);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get element history', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const isInvalidFamily = (family: FamilySelectModel, families: FamilySelectModel[]) => {
        return families.filter(x => x.familyId === family.familyId && x.subFamilyId === family.subFamilyId).length > 1;
    }

    const hasInvalidFamilies = (families: FamilySelectModel[]) => {
        for (const family of families) {
            if (isInvalidFamily(family, families)) {
                return true;
            }
        }
        return false;
    }

    let mediasToSend: CustomFile[] = []
    const onSubmit = async (model: ElementDto) => {
        try {
            Loading.show();
            model.imagesToRemove = [];
            model.imagesIds = [];
            model.attachmentsToCopy = [];

            const allMedias = file ? medias.concat(file) : medias;

            allMedias.forEach((element) => {
                if (element.remove && element.id) {
                    model.imagesToRemove.push(element.id ?? '')
                } else if (!element.remove) {
                    if (element.isMain) {
                        // For copied images (from family): use the source attachment ID
                        // because backend matches AttachmentsToCopy items against MainImageKey
                        if (element.copyFromAttachmentId) {
                            model.mainImageKey = element.copyFromAttachmentId;
                        } else {
                            model.mainImageKey = element.key ?? element.attachmentId;
                        }
                    }

                    if (element.tempFile) {
                        element.tempFile.containerName = element.key
                        mediasToSend.push(element.tempFile)
                        model.imagesIds?.push(element.key)
                    } else if (element.copyFromAttachmentId) {
                        model.attachmentsToCopy?.push(element.copyFromAttachmentId);
                    } else if (type === 'duplicate' && element.attachmentId) {
                        model.attachmentsToCopy?.push(element.attachmentId);
                    }
                }
            });

            if (model.mainImageKey == null && medias.length) {
                model.mainImageKey = medias[0].key ?? medias[0].attachmentId;
            }
            if (file == null || file.id == null) {
                model.deleteAttachment = true;
            }

            const canSaveParameters = validateParameters(parameters, true)
            if (!canSaveParameters) {
                formRef.current?.onInvalid();
                Loading.hide();
                return;
            }

            if (hasInvalidFamilies(model.families ?? [])) {
                Toast.warning(t('elements.duplicated_families'));
                Loading.hide();
                return;
            }

            const trimmedName = model.name?.trim() ?? '';
            if (trimmedName && model.families?.length) {
                const getFamilyLabel = (fam: FamilySelectModel): string => {
                    const parent = families.find(f => f.id === fam.familyId);
                    if (fam.subFamilyId) {
                        const sub = parent?.subFamilies?.find(s => s.id === fam.subFamilyId);
                        if (parent && sub) return `${parent.name} / ${sub.name}`;
                        if (sub) return sub.name;
                    }
                    return parent?.name ?? '';
                };

                for (const fam of model.families) {
                    if (!fam.familyId && !fam.subFamilyId) continue;
                    const dupList = await ElementsService.getAllByCriteria({
                        name: trimmedName,
                        familyId: fam.familyId ?? undefined,
                        subFamilyId: fam.subFamilyId ?? undefined,
                        isElement,
                        isComponent,
                        nameFilterIgnoreFamilyAndReference: true
                    });
                    const duplicate = dupList?.find(e =>
                        e.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
                        (type === 'duplicate' || e.id !== model.id)
                    );
                    if (duplicate) {
                        const msg = t('elements.name_already_exists_in_family', { family: getFamilyLabel(fam) });
                        Toast.warning(msg);
                        form.setError('name', { type: 'manual', message: msg });
                        Loading.hide();
                        return;
                    }
                }
            }

            let paramsToSend: ElementParameterDto[] = [];
            paramsToSend = buildParametersObjectToSend(parameters, [], '', null)
            model.parameters = paramsToSend;
            model.families = model.families?.map(x => { return { ...x, key: x.id } })

            model.isComponent = isComponent;
            model.isElement = isElement;

            if (model && model.id && type !== 'duplicate') {
                await ElementsService.update(model, mediasToSend)
                // navigateTo('details', model.id);
                void getData();
            } else if (model) {
                const id = await ElementsService.create(model, mediasToSend)
                ListingTableCache.save(PAGE_ID, {
                    ...ListingTableCache.get(PAGE_ID, DEFAULT_TABLE_DATA),
                    order: { field: 'updatedDate', orderColumn: 'updated_date', isOrderAsc: false },
                    page: 1,
                    filters: {},
                    lastRowId: id,
                });
                navigateTo('edit', id);
            }
            mediasToSend = [];
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error && error.response && error.response.status === 412 && error.response.data) {
                Toast.warning(t('elements.element_cycle_detected', { name: error.response.data.name }), 8000);
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the element with id: ${id}`, error);
                Toast.error(t('messages.record_save_error'));
            }

            Loading.hide();
        }
    };

    const validateParameters = (params: ElementParameterDto[], canSave: boolean) => {
        params.forEach(param => {
            // Validar apenas se não for BUDGET_ONLY (visível apenas no orçamento, logo não editável no elemento)
            const isVisibleInElement = param.visibility !== ParameterVisibility.BUDGET_ONLY;
            if (param.type === ParameterSectionType.PARAMETER && param.parameterIsRequired && isVisibleInElement) {
                switch (param.parameterType) {
                    case ParameterSectionParameterType.TEXT:
                        if (!param.text) { canSave = false; }
                        return;
                    case ParameterSectionParameterType.IMAGES:
                        if (!param.images || param.images.length === 0 || param.images.every(x => x.remove === true)) { canSave = false; }
                        return;
                    case ParameterSectionParameterType.NUMBER:
                        if (param.number === undefined || param.number === null) { canSave = false; }
                        return;
                    case ParameterSectionParameterType.QUANTITY:
                        if (param.quantity === undefined || param.quantity === null) { canSave = false; }
                        return;
                    case ParameterSectionParameterType.PRODUCTS:
                        if ((!param.products) || param.products.length === 0 || param.products.some(x => x.elementId === undefined || x.elementId === null)) { canSave = false; }
                        return;
                    case ParameterSectionParameterType.NUMBER_MIN_MAX:
                        if (!param.minMax || param.minMax.minValue === undefined || param.minMax.maxValue === undefined || param.minMax.unitId === undefined
                        ) { canSave = false; }
                }
            }
        });

        return canSave
    }

    const buildParametersObjectToSend = (params: ElementParameterDto[], objectToSent: ElementParameterDto[], familyRowId: string, isActive: boolean | null) => {
        let i = 0;
        params.forEach((param: ElementParameterDto) => {
            // Skip BUDGET_ONLY parameters — they must not be saved on elements
            if (param.visibility === ParameterVisibility.BUDGET_ONLY) {
                return;
            }

            if (param.text === '<p> </p>' || param.text === '<p><br></p>') {
                param.text = null;
            }

            let images: AttachmentDto[] = [];
            if (param.images) {
                images = param.images?.filter(x => !x.remove)

                images.forEach((element) => {
                    if (element.tempFile && !element.remove) {
                        element.tempFile.containerName = element.key
                        mediasToSend.push(element.tempFile)
                    }
                });
            }

            objectToSent.push({
                ...param,
                elementFamilyKey: familyRowId || param.elementFamilyId,
                products: param.products?.filter(x => x.elementId || x.checked || x.unitId || x.quantity),
                active: isActive === null ? param.active : isActive,
                position: i
            })
            i++;
        })

        return objectToSent
    }

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`${url}/${typeUrl}/${id}`);
        } else {
            navigate(url);
        }
    }

    const onDelete = async (item: boolean) => {
        if (!item) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            Loading.show();

            await ElementsService.remove(form.getValues())
            navigateTo();

            Loading.hide();
            toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete element', error);
            toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    const definePriceTagLabel = (x: any) => {
        const tag = units.find(y => y.id === x)?.symbol
        setPriceTagLabel(x => '€' + (tag ? `/${tag}` : ''))
        setRefreshKey(y => y + 1)
    }

    const getFamilyParameters = async (subFamilyId: string | null | undefined, familyId: string | null | undefined, elementFamilyId: string, keepPreviousParameters: boolean) => {
        if (!familyId && !subFamilyId && keepPreviousParameters) {
            return;
        }

        const temp: ElementParameterDto[] = []
        parameters.forEach(x => {
            if (x.elementFamilyId === elementFamilyId) {
                if (x.id) {
                    temp.push({ ...x, active: false })
                }
            } else {
                temp.push({ ...x })
            }
        })

        if (!familyId && !subFamilyId && !keepPreviousParameters) {
            setParameters(temp)
            return;
        }

        let params: ElementParameterDto[] = [];
        if (subFamilyId) {
            params = await FamiliesService.getFamilyParameters(subFamilyId)

            if (params.length === 0) {
                const hasFamilyParams = parameters.some(x => x.elementFamilyId === elementFamilyId && x.familyId === familyId)
                if (hasFamilyParams) {
                    return;
                } else {
                    params = await FamiliesService.getFamilyParameters(familyId ?? '')
                }
            }
        } else if (familyId) {
            params = await FamiliesService.getFamilyParameters(familyId)
        }

        if (params.length > 0) {
            params = prepareParams(params, elementFamilyId);
            if (keepPreviousParameters) {
                setParameters([...parameters, ...params])
            } else {
                setParameters([...temp, ...params])
            }
        } else if (!subFamilyId && !keepPreviousParameters) {
            setParameters(temp)
        }
    }

    const prepareParams = (params: ElementParameterDto[], elementFamilyId: string) => {
        params.forEach(param => {
            param.elementFamilyId = elementFamilyId;
            param.familyParameterId = param.id;
            param.id = undefined;
            param.isOpen = true;
        })

        return params
    }

    const onCloseConfirmParametersModal = (isToKeepPrevParameters: boolean) => {
        const itemToSearch = (form.getValues('families') ?? []).find(x => x.id === familyItemChanged?.id)
        void getFamilyParameters(itemToSearch?.subFamilyId, itemToSearch?.familyId, itemToSearch?.id ?? '', isToKeepPrevParameters)
        setShowConfirmParametersModal(false)
        setFamilyItemChanged(null)
    }

    const onCloseRemoveFamilyModal = (isToDeleteFamily: boolean) => {
        if (isToDeleteFamily) {
            const temp: ElementParameterDto[] = []
            parameters.forEach(x => {
                if (x.elementFamilyId === familyItemChanged?.id) {
                    if (x.id && type !== 'duplicate') {
                        temp.push({ ...x, active: false })
                    }
                } else {
                    temp.push({ ...x })
                }
            })

            setParameters(temp)
            form.setValue('families', newFamilies)
            setNewFamilies([])
        }

        setShowConfirmDeleteFamilyModal(false)
        setFamilyItemChanged(null)
    }

    const onFamilyImagesAvailable = useCallback((images: MediaDto[], familyName: string) => {
        if (images && images.length > 0) {
            setFamilyImagesToImport(images);
            setSelectedFamilyName(familyName);
            setShowImportImagesModal(true);
        }
    }, []);

    const renderFamilyItem = (item: FamilySelectModel, i: number) => {
        return (
            <FamilySelector
                key={item.id}
                item={item}
                options={familiesOptions}
                onChange={(items: FamilySelectModel[], previousId: string | null) => {
                    void onFamilyChange(items.find(x => x.familyId)?.familyId ?? undefined);
                    const itemToSearch = items.find(x => x.id === item.id)

                    // if there is any parameters from the family or subfamily that was just changed
                    const hasParams = parameters.find(x => x.elementFamilyId === item.id && x.familyId === previousId);

                    // if the family was deleted and there is any parameters from that family and/or subfamilly
                    const isDeleteFamily = itemToSearch?.familyId === null && parameters.some(x => x.elementFamilyId === itemToSearch?.id)

                    if (isDeleteFamily) {
                        setFamilyItemChanged(item)
                        setShowConfirmDeleteFamilyModal(true)
                        setNewFamilies(items)
                        return
                    } else if (hasParams) {
                        setFamilyItemChanged(item)
                        setShowConfirmParametersModal(true)
                    } else {
                        const lastFamilies = form.getValues('families');
                        const familyAlreadyExisted = lastFamilies?.find(x => x.familyId === itemToSearch?.familyId && x.subFamilyId === itemToSearch?.subFamilyId);
                        if (itemToSearch !== null && !familyAlreadyExisted) {
                            void getFamilyParameters(
                                itemToSearch?.subFamilyId,
                                itemToSearch?.familyId,
                                itemToSearch?.id ?? '',
                                false
                            )
                            applyFamilyPriceExpression(itemToSearch?.familyId, itemToSearch?.subFamilyId);
                        }
                    }

                    form.setValue('families', items);

                    // Auto-populate description from family if creating and description is empty
                    if (!id) {
                        const currentDesc = form.getValues('description');
                        if (!currentDesc || currentDesc === '' || currentDesc === '<p><br></p>') {
                            const selectedFamilyId = itemToSearch?.subFamilyId || itemToSearch?.familyId;
                            if (selectedFamilyId) {
                                const familyData = families.find(f => f.id === selectedFamilyId)
                                    || families.flatMap(f => f.subFamilies || []).find(sf => sf?.id === selectedFamilyId);
                                if (familyData?.description) {
                                    form.setValue('description', familyData.description);
                                }
                            }
                        }
                    }
                }}

                items={form.getValues('families') ?? []}
                disabled={isDetails}
                hasError={isInvalidFamily(item, form.watch('families') ?? [])}
                errorMessage={t('elements.duplicated_family')}
                onImagesAvailable={onFamilyImagesAvailable}
            />
        )
    };

    const onFamilyChange = async (familyId?: string) => {
        if (form.getValues('workTypeId')) {
            return;
        }

        const filteredWorkTypes = await WorkTypesService.getCatalog({ familyId });
        setWorkTypes(filteredWorkTypes);
    }

    const findFamilyPriceExpression = (familyId: string | null | undefined, subFamilyId: string | null | undefined): string | null => {
        if (!familyId) return null;
        const family = families.find(f => f.id === familyId);
        if (!family) return null;

        if (subFamilyId && family.subFamilies?.length) {
            const subFamily = family.subFamilies.find(sf => sf.id === subFamilyId);
            if (subFamily?.priceExpression) return subFamily.priceExpression;
        }

        return family.priceExpression ?? null;
    };

    const applyAndEvaluatePriceExpression = (expr: string) => {
        setPriceExpression(expr);
        form.setValue('priceExpression', expr || undefined);

        const expressionParams = loadPriceExpressionParameters();
        const result = ExpressionSystem.evaluate(expr, expressionParams);
        if (!result.error && result.value !== undefined) {
            isUpdatingFromFormula.current = true;
            previousPriceValue.current = result.value;
            form.setValue('price', result.value);
            onChangePrice(result.value);
        }
    };

    const applyFamilyPriceExpression = (familyId: string | null | undefined, subFamilyId: string | null | undefined) => {
        const familyPriceExpr = findFamilyPriceExpression(familyId, subFamilyId);
        if (!familyPriceExpr) return;

        if (!priceExpression) {
            applyAndEvaluatePriceExpression(familyPriceExpr);
        } else {
            setPendingPriceExpression(familyPriceExpr);
            setShowReplacePriceFormulaModal(true);
        }
    };

    const onCloseReplacePriceFormulaModal = (replace: boolean) => {
        if (replace && pendingPriceExpression) {
            applyAndEvaluatePriceExpression(pendingPriceExpression);
        }
        setPendingPriceExpression(null);
        setShowReplacePriceFormulaModal(false);
    };

    const onChangeFamiliesList = (items: FamilySelectModel[]) => {
        const rowIds: string[] = items.map(x => x.id)
        // if its edit deactivate, if its create delete them from the array
        const temp =
            (id && type !== 'duplicate')
                ? parameters.map(x => {
                    if (!rowIds.includes(x.elementFamilyId ?? '')) {
                        x.active = false;
                    }
                    return x
                })
                : parameters.filter(x => rowIds.includes(x.elementFamilyId ?? ''))
        setParameters([...temp]);
    }

    const onWorkTypeChange = async () => {
        const workTypeId = form.getValues('workTypeId');
        const filteredFamilies = await FamiliesService.getElementsCatalog({ workTypeId, isComponent, isElement });
        setFamilies(filteredFamilies);
    }

    const handleImportImages = (result: ImportFamilyImagesResult) => {
        if (result === 'cancel') {
            setShowImportImagesModal(false);
            return;
        }

        let currentMedias = [...medias];

        if (result === 'replace') {
            currentMedias = currentMedias.map(m => ({ ...m, remove: true }));
        }

        // Check if there's already a main image in current medias (not marked for removal)
        const hasExistingMainImage = currentMedias.some(m => m.isMain && !m.remove);

        const newMedias: AttachmentDto[] = familyImagesToImport.map(img => ({
            id: null,
            key: Utils.newGuid(),
            attachmentId: img.attachmentId,
            url: img.url ?? '',
            name: img.fileName ?? img.name ?? '',
            contentType: img.mimeType ?? '',
            size: img.size ?? 0,
            tempFile: null,
            // Only keep isMain from family if there's no existing main image
            isMain: hasExistingMainImage ? false : img.isMain,
            copyFromAttachmentId: img.attachmentId
        }));

        setMedias([...currentMedias, ...newMedias]);
        setShowImportImagesModal(false);
    };

    const onChangePrice = (price: number) => {
        totalPrice.current = price;
        calculateTotalPrice(parameters)

        setTotalRefreshKey(x => x + 1)
    }

    const calculateTotalPrice = (params: ElementParameterDto[]) => {
        params.forEach((param) => {
            if (param.active && param.type === ParameterSectionType.PARAMETER) {
                if (param.products && param.products.length > 0) {
                    for (const element of param.products) {
                        if (element.checked || isComponent) {
                            totalPrice.current = totalPrice.current + ((element.quantity ?? 0) * (element.price ?? 0))
                        }
                    }
                } else if (param.minMax?.products && param.minMax?.products.length > 0) {
                    for (const element of param.minMax?.products) {
                        if (element.checked) {
                            totalPrice.current = totalPrice.current + ((element.quantity ?? 0) * (element.price ?? 0))
                        }
                    }
                }
            }
        })
    }

    const loadPriceExpressionParameters = () => {
        const expressionParams: { id: string; name: string; value: number | null }[] = [];

        // Add parameters from element's active parameters (NUMBER, QUANTITY, PRICE types)
        // Always include parameters even if they don't have values, so expressions can be deserialized
        parameters.forEach((param) => {
            if (param.active !== false && param.type === ParameterSectionType.PARAMETER) {
                const paramId = param.familyParameterId ?? param.id ?? param.key;
                const paramName = param.parameterName ?? param.title ?? '';

                // NUMBER type - always include so expressions can be deserialized
                if (param.parameterType === ParameterSectionParameterType.NUMBER) {
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId([paramId]),
                        name: ExpressionSystem.formatParameterName([paramName]),
                        value: param.number ?? null
                    });
                }

                // QUANTITY type - always include so expressions can be deserialized
                if (param.parameterType === ParameterSectionParameterType.QUANTITY) {
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId([paramId]),
                        name: ExpressionSystem.formatParameterName([paramName]),
                        value: param.quantity ?? null
                    });
                }

                // PRICE type - always include so expressions can be deserialized
                if (param.parameterType === ParameterSectionParameterType.PRICE) {
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId([paramId]),
                        name: ExpressionSystem.formatParameterName([paramName]),
                        value: param.price?.price ?? null
                    });
                }

                // WEIGHT type - always include so expressions can be deserialized
                if (param.parameterType === ParameterSectionParameterType.WEIGHT) {
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId([paramId]),
                        name: ExpressionSystem.formatParameterName([paramName]),
                        value: param.number ?? null
                    });
                }
            }
        });

        // Add family average values
        const findFamily = (id: string, familiesList: FamilyDto[]): FamilyDto | undefined => {
            for (const f of familiesList) {
                if (f.id === id) return f;
                if (f.subFamilies) {
                    const found = findFamily(id, f.subFamilies);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const currentFamilies = form.getValues('families') ?? [];
        const addedFamilyIds = new Set<string>();
        currentFamilies.forEach((fam) => {
            const familyId = fam.subFamilyId ?? fam.familyId;
            if (familyId) {
                const family = findFamily(familyId, families);
                if (family && !addedFamilyIds.has(family.id)) {
                    addedFamilyIds.add(family.id);
                    // Always add family average parameter so expression can be deserialized even if value is null
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId(['FAMILY', family.id]),
                        name: ExpressionSystem.formatParameterName([`${t('common.parameters_sections.parameters_types.FAMILY_AVERAGE')} (${family.name})`]),
                        value: family.averageValue ?? null
                    });
                }
            }

            // Also register the parent family average so formulas inherited from the parent family resolve correctly
            if (fam.subFamilyId && fam.familyId) {
                const parentFamily = findFamily(fam.familyId, families);
                if (parentFamily && !addedFamilyIds.has(parentFamily.id)) {
                    addedFamilyIds.add(parentFamily.id);
                    expressionParams.push({
                        id: ExpressionSystem.formatParameterId(['FAMILY', parentFamily.id]),
                        name: ExpressionSystem.formatParameterName([`${t('common.parameters_sections.parameters_types.FAMILY_AVERAGE')} (${parentFamily.name})`]),
                        value: parentFamily.averageValue ?? null
                    });
                }
            }
        });

        // Add element type coefficient if selected — coefficient stored as percentage; expose as multiplier
        const elementTypeId = form.getValues('elementTypeId');
        const selectedElementType = elementTypeId ? elementTypes.find(et => et.id === elementTypeId) : undefined;
        if (selectedElementType) {
            const typePercent = selectedElementType.coefficient ?? 0;
            expressionParams.push({
                id: ExpressionSystem.formatParameterId(['ELEMENT_TYPE_COEFFICIENT', selectedElementType.id]),
                name: ExpressionSystem.formatParameterName([`${t('common.parameters_sections.parameters_types.ELEMENT_TYPE_COEFFICIENT')} (${selectedElementType.name})`]),
                value: 1 + typePercent / 100
            });
        }

        // Sum family coefficients (percentages) for the selected families (effective family = subFamily ?? family) — mirrors backend RecalculateElementPricesJob
        const countedFamilyCoefficientIds = new Set<string>();
        let familyCoefficientSum = 0;
        currentFamilies.forEach((fam) => {
            const effectiveId = fam.subFamilyId ?? fam.familyId;
            if (!effectiveId) return;
            const f = findFamily(effectiveId, families);
            if (!f || countedFamilyCoefficientIds.has(f.id)) return;
            countedFamilyCoefficientIds.add(f.id);
            familyCoefficientSum += f.coefficient ?? 0;
        });

        const typePercentForGeneric = selectedElementType?.coefficient ?? 0;

        // Generic coefficient: multiplier built from (type% + sum of family%) / 100
        expressionParams.push({
            id: ExpressionSystem.formatParameterId(['ELEMENT_TYPE_COEFFICIENT']),
            name: ExpressionSystem.formatParameterName([t('common.parameters_sections.parameters_types.ELEMENT_TYPE_COEFFICIENT')]),
            value: 1 + (typePercentForGeneric + familyCoefficientSum) / 100
        });

        // Other catalog elements: the formula can reference another element's price (searchable by
        // name or reference). The id encodes the element id (ELEMREF__<id>); the backend re-evaluates
        // and cascades when the referenced element's price changes.
        catalogElements.forEach((el) => {
            if (el.id === id) return;
            const displayName = el.reference ? `${el.reference} - ${el.name}` : el.name;
            expressionParams.push({
                id: ExpressionSystem.formatParameterId(['ELEMREF', el.id]),
                name: ExpressionSystem.formatParameterName([displayName]),
                value: el.price ?? null
            });
        });

        return expressionParams;
    }

    const getHistoryReasonTranslationKey = (reason: ElementHistoryReason): string => {
        const reasonKeys: Record<ElementHistoryReason, string> = {
            [ElementHistoryReason.MANUAL_UPDATE]: 'MANUAL_UPDATE',
            [ElementHistoryReason.BULK_PRICE_UPDATE]: 'BULK_PRICE_UPDATE',
            [ElementHistoryReason.EXPRESSION_RECALCULATION]: 'EXPRESSION_RECALCULATION',
            [ElementHistoryReason.INITIAL_CREATION]: 'INITIAL_CREATION',
            [ElementHistoryReason.FAMILY_AVERAGE_RECALCULATION]: 'FAMILY_AVERAGE_RECALCULATION',
            [ElementHistoryReason.COEFFICIENT_RECALCULATION]: 'COEFFICIENT_RECALCULATION',
            [ElementHistoryReason.REFERENCED_ELEMENT_RECALCULATION]: 'REFERENCED_ELEMENT_RECALCULATION'
        };
        return `elements.history_reasons.${reasonKeys[reason]}`;
    };

    const getHistoryReasonText = (item: ElementHistoryDto): string => {
        const key = getHistoryReasonTranslationKey(item.reason);

        // For referenced-element recalculations, show which element (and to what price) caused the change.
        if (item.reason === ElementHistoryReason.REFERENCED_ELEMENT_RECALCULATION && item.referencedElementName) {
            const price = (item.referencedElementPrice ?? 0).toLocaleString(i18n.language, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return t(key, { element: item.referencedElementName, price });
        }

        return t(key);
    };

    const renderHistoryItem = (item: ElementHistoryDto) => (
        <div>
            <Text className={styles.dateTime}>
                <DateFormat value={item.date} format={DATE_TIME_FORMAT_DEFAULT} /> {' | ' + item.userName}
            </Text>
            <Text className={styles.price}>
                {t('elements.price_no_tax') + ': '}<MoneyFormat value={item.price} suffix={' €' + (item.unit ? `/${item.unit}` : '')} />
            </Text>
            <Text className={styles.reason}>
                {getHistoryReasonText(item)}
            </Text>
        </div>
    );
    const onItemChange = (item: AtLeast<FlattenedItem<ElementParameterDto>, 'key'>,reason: 'change' | 'remove' | 'change-self-and-children') => {
        if (reason === 'change') {
            setParameters(parameters.map((section) => {
                if (section.key === item.key) {
                    return { ...section, ...item };
                }
                return section;
            }));
        }
    }

    const onMoveItem = (key: string, type: 'up' | 'down') => {
        const newLines = onMove(parameters,key,type)

        setParameters([...newLines])
    }

    const recalculatePriceExpression = useCallback(() => {
        if (!priceExpression) return;

        const expressionParams = loadPriceExpressionParameters();
        const result = ExpressionSystem.evaluate(priceExpression, expressionParams);

        if (!result.error && result.value !== undefined) {
            // Set flag to indicate this is a formula update
            isUpdatingFromFormula.current = true;
            previousPriceValue.current = result.value;
            form.setValue('price', result.value);
            onChangePrice(result.value);
        }
    }, [priceExpression, parameters, families]);

    const checkIfCoefficientUsedInFormula = (elementTypeId: string | null | undefined): boolean => {
        if (!priceExpression) return false;

        // Check if the formula contains ANY element type coefficient
        // Pattern: M_ELEMENT_TYPE_COEFFICIENT__<any-id>_E
        const hasCoefficientPattern = /M_ELEMENT_TYPE_COEFFICIENT__[^_]+_E/.test(priceExpression);

        return hasCoefficientPattern;
    };

    const updateCoefficientInFormula = (oldElementTypeId: string | null, newElementTypeId: string | null, remove: boolean) => {
        if (!priceExpression) return;

        let updatedExpression = priceExpression;

        // Find ANY coefficient pattern in the formula and replace with the new one
        // Pattern: M_ELEMENT_TYPE_COEFFICIENT__<any-id>_E
        const coefficientPattern = /M_ELEMENT_TYPE_COEFFICIENT__[^_]+_E/g;

        if (remove) {
            // Replace coefficient with 1
            updatedExpression = updatedExpression.replace(coefficientPattern, '1');
        } else if (newElementTypeId) {
            const newElementType = elementTypes.find(et => et.id === newElementTypeId);
            if (newElementType) {
                const newCoefficientId = ExpressionSystem.formatParameterId(['ELEMENT_TYPE_COEFFICIENT', newElementType.id]);
                const newSerializedId = ExpressionSystem.serializeParameterMentionId(newCoefficientId);
                updatedExpression = updatedExpression.replace(coefficientPattern, newSerializedId);
            }
        } else {
            // If no new element type is selected, replace coefficient with 1
            updatedExpression = updatedExpression.replace(coefficientPattern, '1');
        }

        setPriceExpression(updatedExpression);
        form.setValue('priceExpression', updatedExpression, { shouldValidate: true, shouldDirty: true });

        // Recalculate with the updated expression
        const expressionParams = loadPriceExpressionParameters();
        const result = ExpressionSystem.evaluate(updatedExpression, expressionParams);

        if (!result.error && result.value !== undefined) {
            // Set flag to indicate this is a formula update
            isUpdatingFromFormula.current = true;
            previousPriceValue.current = result.value;
            form.setValue('price', result.value);
            onChangePrice(result.value);
        }
    };

    const onCloseConfirmUpdateCoefficientModal = (action: 'update' | 'remove' | 'cancel') => {
        if (action === 'update') {
            updateCoefficientInFormula(previousElementTypeId, newElementTypeId, false);
        } else if (action === 'remove') {
            updateCoefficientInFormula(previousElementTypeId, newElementTypeId, true);
        }

        if (action !== 'cancel') {
            form.setValue('elementTypeId', newElementTypeId ?? undefined);
        }

        setShowConfirmUpdateCoefficientModal(false);
        setPreviousElementTypeId(null);
        setNewElementTypeId(null);
    };

    const handleElementTypeChange = (value: string | null | undefined) => {
        const currentElementTypeId = form.getValues('elementTypeId');
        const isUsed = checkIfCoefficientUsedInFormula(currentElementTypeId);

        if (currentElementTypeId && isUsed) {
            setPreviousElementTypeId(currentElementTypeId);
            setNewElementTypeId(value ?? null);
            setShowConfirmUpdateCoefficientModal(true);
        } else {
            form.setValue('elementTypeId', value ?? undefined);

            // Recalculate if formula uses generic coefficient (from family)
            if (priceExpression && priceExpression.includes('M_ELEMENT_TYPE_COEFFICIENT_E')) {
                recalculatePriceExpression();
            }
        }
    };

    useEffect(() => {
        // Recalculate price expression when parameters change
        recalculatePriceExpression();

        totalPrice.current = form.getValues('price') ?? 0;
        calculateTotalPrice(parameters)

        setTotalRefreshKey(x => x + 1)
    }, [parameters]);

    const onClickBody = (ev: MouseEvent) => {
        if (ev && ev.target) {
            const target = ev.target as HTMLElement;
            const targetId = target.id;
            const targetParent = target.parentElement;

            if (
                targetId.includes('react-select-') ||
                (target.className && target.className.includes && target.className.includes('LoadingProvider')) ||
                (targetParent && targetParent.className && targetParent.className.includes && targetParent.className.includes('OptionTitleSubTitle'))
            ) {
                return;
            }
        }

        if (selectedSectionKey) {
            setSelectedSectionKey('');
        }
    }

    useEffect(() => {
        document.body.addEventListener('click', onClickBody);

        return function cleanup() {
            window.removeEventListener('click', onClickBody);
        }
    }, [selectedSectionKey, setSelectedSectionKey]);

    const unitsOptions = units.map((x: UnitDto) => {
        return {
            label: x.name,
            value: x.id,
        }
    })

    const taxesOptions = taxes.map((x: TaxDto) => {
        return {
            label: x.description,
            value: x.id
        }
    })

    const elementTypesOptions = elementTypes.map((x: ElementTypeDto) => {
        return {
            label: x.name,
            value: x.id
        }
    })

    const familiesOptions: FamilySelectInputOption[] = families.map((x: FamilyDto) => {
        return {
            label: x.name,
            value: x.id,
            isComponent: x.isComponent,
            isElement: x.isElement,
            images: x.images,
            subFamilies: x.subFamilies?.map((y: FamilyDto) => {
                return {
                    label: y.name,
                    value: y.id,
                    subFamilies: [],
                    isComponent: x.isComponent,
                    isElement: x.isElement,
                    images: y.images
                }
            }) ?? []
        }
    }) ?? [];

    if (!canRead) {
        return null;
    }
    const screenTitleLabel = isElement ? t('elements.title_elements') : t('elements.title_components');
    return (<ScreenTitle title={screenTitleLabel}>
        <PageBreadcrumbsPortal
            breadcrumbs={[
                { name: t('home.title'), url: '/' },
                { name: screenTitleLabel, url },
                { name: title, url: currentUrl },
            ]}
        />

        <PageHeader title={titlePrefix} subTitle={entityLabel} informationText={t('common.go_back')} onGoBack={() => navigateTo()}>
            <Button variant='forth' fw className={styles.totalPriceButton}>
                <span className={styles.totalPriceLabel}>{t('elements.total_price')}</span>
                <span key={totalRefreshKey} className={styles.totalPrice}>
                    <MoneyFormat value={totalPrice.current} suffix={'€'} />
                </span>
                <InfoIcon className={styles.infoIcon} onClick={() => setShowTotalPriceInformationModal(true)} />
            </Button>

            { type !== 'create' && <Button fw variant='secondary' onClick={() => onOpenHistory()}> <FaHistory /> </Button> }

            {canWrite && !isDetails && <Button type='submit' form='elementForm'>{t('common.save')}</Button>}
            {canWrite && isDetails && <Button fw onClick={() => { navigateTo('duplicate', id); }}>{t('common.duplicate')}</Button>}
            {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
            {canWrite && isDetails && <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}> <FaTrashAlt /> </Button>}
        </PageHeader>
        <PageContainer addBottomSpace>
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate id="elementForm" ref={formRef}>
                <Row className="mb-3">
                    {isElement && <Col md={12} lg={6} className={styles.marginBottomMd}>
                        <Label space>{t('elements.element_type')}{!isDetails ? '*' : ''}</Label>
                        <SelectInputController
                            control={form.control}
                            name='elementTypeId'
                            options={elementTypesOptions || []}
                            rules={{ required: true }}
                            disabled={isDetails}
                            onChange={handleElementTypeChange}
                            hasError={Boolean(errors.elementTypeId)}
                        />
                        <InputError error={errors.elementTypeId} />
                    </Col>}
                    <Col md={12} lg={6}>
                        <Label space>{t('elements.work_type')}</Label>
                        <SelectInputController
                            control={form.control}
                            name='workTypeId'
                            options={workTypes || []}
                            rules={{ required: false }}
                            disabled={isDetails}
                            onChange={onWorkTypeChange}
                            hasError={Boolean(errors.workTypeId)}
                        />
                        <InputError error={errors.workTypeId} />
                    </Col>
                </Row>
                {(!isDetails || form.getValues('families')?.length) ?
                    <Row className="mb-3">
                        <Col>
                            <MultiLineDynamicListController
                                control={form.control}
                                name='families'
                                onChange={(e) => onChangeFamiliesList(e)}
                                disabled={isDetails}
                                renderItem={renderFamilyItem}
                                labelAdd={t('elements.add_new_family')}
                                askBeforeDelete={true}
                                deleteModalItemName={t('elements.modals.the_family')}
                                deleteModalMessage={t('elements.modals.will_delete_parameters')} />
                        </Col>
                    </Row>
                    : undefined
                }
                <Row className="mb-3">
                    <Col>
                        <Label space>{t('elements.name')}{!isDetails ? '*' : ''}</Label>
                        <TextInputController
                            name='name' control={form.control}
                            placeholder={t('elements.name')} disabled={isDetails}
                            rules={{ required: true, maxLength: 250 }}
                            hasError={Boolean(errors.name)} />
                        <InputError error={errors.name} maxLength={250} />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Label space>{t('elements.image')}</Label>
                        <FilesUploader
                            files={medias}
                            type='images'
                            onChange={setMedias}
                            containerName={(f) => f.key ?? ''}
                            canSetMain
                            disabled={isDetails}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={6} lg={3} className={styles.marginBottomMd}>
                        <Label space>{t('elements.reference')}</Label>
                        <TextInputController
                            name='reference' control={form.control}
                            placeholder={t('elements.reference')} disabled={isDetails}
                            rules={{ required: false, maxLength: 250 }}
                            hasError={Boolean(errors.reference)} />
                        <InputError error={errors.reference} maxLength={250} />
                    </Col>
                    <Col md={6} lg={3} className={styles.marginBottomMd}>
                        <Label space>{t('elements.referenceSage')}</Label>
                        <TextInputController
                            name='referenceSage' control={form.control}
                            placeholder={t('elements.referenceSage')} disabled={isDetails}
                            rules={{ required: false, maxLength: 250 }}
                            hasError={Boolean(errors.referenceSage)} />
                        <InputError error={errors.referenceSage} maxLength={250} />
                    </Col>
                    <Col md={6} lg={3} className={styles.marginBottomMd}>
                        <Label space>{t('elements.unit')}{!isDetails ? '*' : ''}</Label>
                        <SelectInputController
                            control={form.control}
                            name='unitId'
                            options={unitsOptions || []}
                            rules={{ required: true }}
                            disabled={isDetails}
                            onChange={(x) => definePriceTagLabel(x)}
                            hasError={Boolean(errors.unitId)}
                        />
                        <InputError error={errors.unitId} />
                    </Col>
                    <Col md={6} lg={3} className={styles.marginBottomSm}>
                        <Label space>{t('elements.price_no_tax')}</Label>
                        <MoneyInputController
                            name='price'
                            control={form.control}
                            unitPrefix={priceTagLabel}
                            decimalScale={6}
                            displayDecimalScale={2}
                            onChange={(x) => {
                                // When user manually changes the value, clear the formula
                                // Only clear if the value actually changed and it's not a formula update
                                const valueChanged = previousPriceValue.current !== x;
                                if (priceExpression && !isUpdatingFromFormula.current && valueChanged) {
                                    setPriceExpression(null);
                                    form.setValue('priceExpression', undefined);
                                }
                                // Update previous value and reset flag
                                previousPriceValue.current = x;
                                isUpdatingFromFormula.current = false;
                                onChangePrice(x ?? 0);
                            }}
                            disabled={isDetails}
                            hasError={Boolean(errors.price)}
                            expressionButton={{
                                title: ExpressionSystem.formatParameterName([t('elements.price_no_tax')]),
                                value: priceExpression ?? '',
                                loadParameters: () => loadPriceExpressionParameters(),
                                onChange: (expr, value) => {
                                    setPriceExpression(expr);
                                    form.setValue('priceExpression', expr || undefined);
                                    if (value !== null && value !== undefined) {
                                        // Set flag to indicate this is a formula update
                                        isUpdatingFromFormula.current = true;
                                        previousPriceValue.current = value;
                                        form.setValue('price', value);
                                        onChangePrice(value);
                                    }
                                },
                            }}
                        />
                        <InputError error={errors.price} />
                    </Col>
                    <Col md={6} lg={3} className={styles.marginBottomSm}>
                        <Label space>{t('elements.tax')}</Label>
                        <SelectInputController
                            control={form.control}
                            name='taxId'
                            options={taxesOptions || []}
                            rules={{ required: false }}
                            disabled={isDetails}
                            hasError={Boolean(errors.taxId)}
                        />
                        <InputError error={errors.taxId} />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Label space>{t('elements.technical_file')}</Label>
                        <FileInputController
                            name='attachment'
                            value={file}
                            control={form.control}
                            accept={{
                                'application/pdf': ['.pdf'],
                            }}
                            onChange={setFile}
                            disabled={isDetails}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Label space>{t('elements.description')}</Label>
                        <RichInputController
                            name='description' control={form.control}
                            placeholder={t('elements.description')} disabled={isDetails}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Label space>{t('elements.budget_pdf_visibility')}{!isDetails ? '*' : ''}</Label>
                        <SelectInputController
                            control={form.control}
                            name='budgetPdfVisibility'
                            options={[
                                {
                                    label: t('elements.budget_pdf_visibility_options.element_and_price_visible'),
                                    value: BudgetPDFVisibility.ELEMENT_AND_PRICE_VISIBLE
                                },
                                {
                                    label: t('elements.budget_pdf_visibility_options.price_invisible'),
                                    value: BudgetPDFVisibility.PRICE_INVISIBLE
                                },
                                {
                                    label: t('elements.budget_pdf_visibility_options.element_invisible'),
                                    value: BudgetPDFVisibility.ELEMENT_INVISIBLE
                                },
                            ]}
                            rules={{ required: true }}
                            disabled={isDetails}
                            hasError={Boolean(errors.budgetPdfVisibility)}
                        />
                        <InputError error={errors.budgetPdfVisibility} />
                    </Col>
                </Row>

                <div className={styles.sectionsContainer}>
                    <ElementParameterDataProvider.Provider value={{
                        selectedSectionKey,
                        parameters,
                        isSubmited: form.formState.isSubmitted,
                        hideBaseCheckBox: isComponent
                    }}>
                        <SortableTree
                            items={displayParameters}
                            onItemsChanged={(newItems) => setParameters(newItems as ElementParameterDto[])}
                            onItemChange={onItemChange}
                            onMoveItem={onMoveItem}
                            onSelectItem={setSelectedSectionKey}
                            TreeItemComponent={ElementParameterLine}
                            canHaveChildren={(item) => (item.type === ParameterSectionType.SECTION)}
                            canRootHaveChildren={true}
                            dropAnimation={null}
                            indentationWidth={20}
                            disableSorting={isDetails}
                        />
                    </ElementParameterDataProvider.Provider>
                </div>
            </Form>
        </PageContainer>

        <ConfirmDeleteModal
            itemName={form.getValues('name')}
            isOpen={showConfirmDeleteModal}
            onClose={(result) => onDelete(result)}
        />
        <YesNoModal
            title={t('elements.modals.keep_or_delete_old_parameters_title')}
            message={t('elements.modals.keep_or_delete_old_parameters_message')}
            isOpen={showConfirmParametersModal}
            onClose={(x) => onCloseConfirmParametersModal(x)}
        />

        <ConfirmDeleteModal
            itemName={t('elements.modals.the_family')}
            secundaryMessage={t('elements.modals.will_delete_parameters')}
            isOpen={showConfirmDeleteFamilyModal}
            onClose={(isToDeleteFamily) => onCloseRemoveFamilyModal(isToDeleteFamily)}
        />

        <InformationModal
            message={t('elements.modals.total_price_info')}
            isOpen={showTotalPriceInformationModal}
            onClose={() => setShowTotalPriceInformationModal(false)}
        />

        <HistoryModal
            title={t('elements.modals.price_history')}
            isOpen={showHistoryModal}
            items={elementHistory}
            renderItem={renderHistoryItem}
            onClose={() => setShowHistoryModal(false)}
        />

        <ImportFamilyImagesModal
            title={t('elements.modals.import_family_images_title')}
            familyName={selectedFamilyName}
            imageCount={familyImagesToImport.length}
            hasExistingImages={medias.filter(m => !m.remove).length > 0}
            isOpen={showImportImagesModal}
            onClose={handleImportImages}
        />

        {showConfirmUpdateCoefficientModal && (
            <Modal isOpen={true} style={{ content: { minWidth: 400 } }} onRequestClose={() => onCloseConfirmUpdateCoefficientModal('cancel')}>
                <div>
                    <ModalHeader>
                        <Header>{t('elements.modals.update_coefficient_title').toUpperCase()}</Header>
                    </ModalHeader>
                    <ModalBody>
                        <Text>{t('elements.modals.update_coefficient_message1')}</Text>
                        <Text style={{ marginTop: '1rem' }}>{t('elements.modals.update_coefficient_message2')}</Text>
                    </ModalBody>
                    <ModalFooter className="text-right">
                        <Button fw variant="secondary" onClick={() => onCloseConfirmUpdateCoefficientModal('remove')}>
                            {t('elements.modals.update_coefficient_one')}
                        </Button>
                        <Button onClick={() => onCloseConfirmUpdateCoefficientModal('update')}>
                            {t('elements.modals.update_coefficient_new')}
                        </Button>
                    </ModalFooter>
                </div>
            </Modal>
        )}

        <YesNoModal
            title={t('common.question')}
            message={t('families.family.replace_price_formula')}
            isOpen={showReplacePriceFormulaModal}
            onClose={onCloseReplacePriceFormulaModal}
        />
    </ScreenTitle>)
}

export default ElementScreen;
